import React from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/new-components/ui/button';
import type { BrandingData } from './SandboxBrandingStep';

interface SandboxReviewStepProps {
  companyName: string;
  slug: string;
  branding: BrandingData;
  investorEmails: string[];
  isLaunching: boolean;
  launchError: string | null;
  onLaunch: () => void;
  onBack: () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SandboxReviewStep({
  companyName,
  slug,
  branding,
  investorEmails,
  isLaunching,
  launchError,
  onLaunch,
  onBack,
}: SandboxReviewStepProps) {
  const validEmails = investorEmails.filter((e) => EMAIL_PATTERN.test(e));

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">Review &amp; launch</h2>
        <p className="mt-1 text-muted-foreground">
          Double-check your setup before launching your sandbox.
        </p>
      </div>

      {/* Summary */}
      <div className="rounded-xl border border-border divide-y divide-border overflow-hidden">
        {/* Logo + Name */}
        <div className="p-4 flex items-center gap-4">
          {branding.logoPreviewUrl ? (
            <div className="w-[80px] h-[40px] flex-shrink-0 rounded overflow-hidden bg-white border border-border/40 flex items-center justify-center">
              <img
                src={branding.logoPreviewUrl}
                alt="Logo"
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ) : (
            <div
              className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-lg"
              style={{ backgroundColor: branding.primaryColor }}
            >
              {companyName.charAt(0).toUpperCase() || '?'}
            </div>
          )}
          <div>
            <p className="font-semibold text-foreground">{companyName}</p>
            <p className="text-sm text-muted-foreground font-mono">/{slug}</p>
          </div>
        </div>

        {/* Colors + Font */}
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground w-28">Primary colour</span>
            <div
              className="w-6 h-6 rounded-full border border-border"
              style={{ backgroundColor: branding.primaryColor }}
            />
            <span className="text-sm font-mono">{branding.primaryColor}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground w-28">Secondary colour</span>
            <div
              className="w-6 h-6 rounded-full border border-border"
              style={{ backgroundColor: branding.secondaryColor }}
            />
            <span className="text-sm font-mono">{branding.secondaryColor}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground w-28">Font</span>
            <span className="text-sm" style={{ fontFamily: branding.fontFamily }}>
              {branding.fontFamily}
            </span>
          </div>
        </div>

        {/* Investors */}
        <div className="p-4">
          <p className="text-sm text-muted-foreground mb-2">Investors ({validEmails.length})</p>
          {validEmails.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">None — you can invite investors after launch.</p>
          ) : (
            <ul className="space-y-1">
              {validEmails.map((email) => (
                <li key={email} className="text-sm font-mono text-foreground">
                  {email}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Trial limits reminder */}
      <div className="rounded-lg bg-muted/60 border border-border px-4 py-3 text-sm text-muted-foreground">
        <span className="font-medium text-foreground">Trial limits: </span>
        7-day trial &middot; 10 e-signs &middot; 3 offerings &middot; 6 properties
      </div>

      {/* Launch error */}
      {launchError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{launchError}</span>
        </div>
      )}

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} disabled={isLaunching} className="flex-1">
          Back
        </Button>
        <Button onClick={onLaunch} disabled={isLaunching} className="flex-1">
          {isLaunching ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Launching…
            </>
          ) : (
            'Launch Sandbox'
          )}
        </Button>
      </div>
    </div>
  );
}
